import { memo, useMemo, useCallback } from 'react'
import { Input } from '@shared/components/ui/input'
import { Label } from '@shared/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select'
import { cn } from '@shared/lib/utils'
import { CURRENCIES, CURRENCY_SYMBOLS } from '@features/products/components/sheets/steps/constants'
import { INPUTS, SELECTS, COMPOUND_INPUT, TYPOGRAPHY } from './styles'

interface CurrencyAmountInputProps {
  id?: string
  label?: string
  required?: boolean
  price: string
  currency: string
  onPriceChange: (price: string) => void
  onCurrencyChange?: (currency: string) => void
  placeholder?: string
  className?: string
  showCurrencySelector?: boolean
  currencySymbol?: string
  description?: string
}

export const CurrencyAmountInput = memo<CurrencyAmountInputProps>(
  function CurrencyAmountInput({
    id = "amount",
    label = "Amount",
    required,
    price,
    currency,
    onPriceChange,
    onCurrencyChange,
    placeholder = "0.00",
    className = "",
    showCurrencySelector = true,
    currencySymbol,
    description,
  }) {
    const resolvedCurrencySymbol = useMemo(() =>
      currencySymbol || CURRENCY_SYMBOLS[currency] || '$',
      [currency, currencySymbol]
    )

    const handlePriceChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      // Strip anything that isn't a digit or decimal point; allow only one decimal
      const sanitized = e.target.value
        .replace(/[^0-9.]/g, '')
        .replace(/(\..*)\./g, '$1')
      onPriceChange(sanitized)
    }, [onPriceChange])

    const formattedPreview = useMemo(() => {
      if (!price || price === '0' || price === '0.00') return null
      const num = parseFloat(price)
      if (isNaN(num)) return null
      return `${resolvedCurrencySymbol}${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`
    }, [price, resolvedCurrencySymbol, currency])

    // Render currency symbol (extracted for reuse)
    const CurrencySymbolElement = (
      <span className={COMPOUND_INPUT.currencySymbol}>
        {resolvedCurrencySymbol}
      </span>
    )

    // When used as simple currency input (no selector)
    if (!showCurrencySelector) {
      return (
        <div className={cn('space-y-1.5', className)}>
          {label && (
            <Label htmlFor={id} className={TYPOGRAPHY.label}>
              {label}{required && <span className="text-destructive ml-0.5">*</span>}
            </Label>
          )}
          <div className="relative">
            {CurrencySymbolElement}
            <Input
              id={id}
              type="text"
              inputMode="decimal"
              value={price}
              onChange={handlePriceChange}
              className={INPUTS.currency}
              placeholder={placeholder}
              aria-label={label || "Currency amount"}
            />
          </div>
          {description && (
            <p className="text-xs text-muted-foreground font-light leading-tight">{description}</p>
          )}
        </div>
      )
    }

    // Full currency input with selector
    return (
      <div className={cn('space-y-1.5', className)}>
        {label && <Label htmlFor={id} className={TYPOGRAPHY.label}>{label}</Label>}
        <div
          className={COMPOUND_INPUT.wrapper}
          role="group"
          aria-label={`${label} with currency selector`}
        >
          <div className="flex-1 relative">
            {CurrencySymbolElement}
            <Input
              id={id}
              type="text"
              inputMode="decimal"
              placeholder={placeholder}
              value={price}
              onChange={handlePriceChange}
              className={cn(INPUTS.currencyWithSelect, COMPOUND_INPUT.inputSection)}
              aria-label={label || "Currency amount"}
            />
          </div>
          <Select value={currency} onValueChange={onCurrencyChange}>
            <SelectTrigger
              className={cn(SELECTS.currency, COMPOUND_INPUT.selectSection)}
              aria-label="Currency selector"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={SELECTS.currencyContent}>
              {CURRENCIES.map((curr) => (
                <SelectItem key={curr} value={curr}>{curr}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {description ? (
          <p className="text-xs text-muted-foreground font-light leading-tight">{description}</p>
        ) : formattedPreview ? (
          <p className="text-xs text-muted-foreground/60 font-light leading-tight">{formattedPreview}</p>
        ) : null}
      </div>
    )
  }
)

CurrencyAmountInput.displayName = 'CurrencyAmountInput'
